/**
 * Iter 58 — Mobile-only restricted shell.
 *
 * Routes under /m use this layout — desktop ERP at <Layout> is hidden on phones.
 * Bottom-nav tabs visible everywhere inside /m. Available tabs depend on the
 * current user's role + department.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Home, Clock, Users, LogOut, ChevronDown, UserCircle2, UserPlus, ShieldAlert, Activity, UserCog, CheckSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost } from '../lib/api';
import { clearAuth } from '../lib/storage';
import OnboardingWizard from './OnboardingWizard';
import OfflineIndicator from './OfflineIndicator';
import { toast } from 'sonner';

const ONBOARDING_DISMISS_KEY = 'ellora_onboarding_dismissed';

export function canRegisterLabour(user) {
  // Per user policy, Labour Registration is open to every
  // authenticated mobile user. Permission gating happens server-side on submit.
  return !!user;
}

// GRN creation maps to the `inventory` module (require_write("inventory")
// server-side). Show the Home tile only to users who can actually create a GRN.
export function canCreateGRN(user) {
  if (!user) return false;
  if ((user.role || '').toLowerCase() === 'super_admin') return true;
  const mods = user.allowed_modules || [];
  return mods.includes('*') || mods.includes('inventory');
}

// Labour Performance entry is for site supervisors — gated to labour_attendance
// write (the same permission the kiosk uses to record attendance).
export function canTrackPerformance(user) {
  if (!user) return false;
  if ((user.role || '').toLowerCase() === 'super_admin') return true;
  const mods = user.allowed_modules || [];
  return mods.includes('*') || mods.includes('labour_attendance');
}

export default function MobileLayout() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close the avatar menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('touchstart', onClick);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await apiPost('/auth/logout', {});
    } catch {
      // ignore — server may already have revoked
    }
    // Wipes both localStorage AND native Capacitor Preferences so the next
    // app launch shows the login screen instead of auto-unlocking.
    await clearAuth();
    toast.success('Logged out');
    navigate('/login', { replace: true });
    setTimeout(() => window.location.reload(), 50);
  };

  // Same logic as desktop Layout — onboarding must be visible on phones too.
  useEffect(() => {
    if (!user) return;
    const isSuper = user.role === 'super_admin';
    if (isSuper) {
      if (localStorage.getItem(ONBOARDING_DISMISS_KEY) === '1') return;
      apiGet('/hr/employees').catch(() => []).then((emps) => {
        const eList = Array.isArray(emps) ? emps : (emps?.employees || []);
        if (eList.length === 0) setShowOnboarding(true);
      });
    } else {
      Promise.all([
        apiGet('/hr/me').catch(() => null),
        apiGet('/hr/policies/me/pending').catch(() => ({ pending: [] })),
      ]).then(([me, pendRes]) => {
        const emp = me?.employee;
        const profileOk = !!(emp && emp.phone && emp.emergency_contact);
        // Password change is enforced at LOGIN (Login.jsx); not an onboarding gate.
        const policiesOk = (pendRes?.pending || []).length === 0;
        const tourOk = JSON.parse(localStorage.getItem('ellora_onboarding_status') || '{}').tour === true;
        if (!(profileOk && policiesOk && tourOk)) {
          setShowOnboarding(true);
        }
      });
    }
  }, [user]);

  const tabs = [
    { to: '/m', icon: Home, label: 'Home', end: true },
    { to: '/m/attend', icon: Clock, label: 'Punch' },
    { to: '/m/labour', icon: Users, label: 'Kiosk' },
    ...(canRegisterLabour(user) ? [{ to: '/m/labour-register', icon: UserPlus, label: 'Register' }] : []),
    { to: '/m/safety', icon: ShieldAlert, label: 'Safety' },
    { to: '/m/ess', icon: UserCog, label: 'My ESS' },
    { to: '/m/approvals', icon: CheckSquare, label: 'Approvals' },
    ...(canTrackPerformance(user) ? [{ to: '/m/labour-performance', icon: Activity, label: 'Perf' }] : []),
  ];
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fdfdfd] via-white to-[#eef0f6] flex flex-col">
      {/* Brand header bar — safe-area aware (Iter 109): respects status-bar / notch /
          camera-cutout via env(safe-area-inset-top). Requires `viewport-fit=cover`
          in index.html which we set in the same patch. */}
      <header
        className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/70 flex items-center justify-between px-4"
        style={{
          // Native Android: the WebView is already margined below the status bar
          // by Capacitor (adjustMarginsForEdgeToEdge="auto"), so env() here is 0
          // and this is just the normal header padding. On web/PWA with a notch,
          // env(safe-area-inset-top) adds the needed offset.
          paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
          paddingBottom: '0.75rem',
        }}
      >
        <img
          src="/assets/elr-logo-teal.png"
          alt="ELR Industries"
          className="h-12 w-auto"
          data-testid="m-brand-logo"
        />
        <div className="relative flex items-center gap-2" ref={menuRef}>
          <OfflineIndicator />
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-full hover:bg-slate-100 active:scale-95 transition"
            data-testid="m-user-menu-btn"
          >
            <div className="w-8 h-8 rounded-full bg-[#1F7A6E] text-white flex items-center justify-center text-xs font-display font-bold">
              {(user?.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <ChevronDown size={12} className={`text-slate-500 transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1.5 w-60 bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden"
              data-testid="m-user-menu"
            >
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="font-display font-bold text-sm truncate">{user?.name || 'User'}</div>
                <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
                {(user?.department || user?.role) && (
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {user?.department}{user?.role ? ` · ${user.role.replace(/_/g, ' ')}` : ''}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); navigate('/m/profile'); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-slate-50 active:bg-slate-100 text-left"
                data-testid="m-menu-profile"
              >
                <UserCircle2 size={14} className="text-slate-500" /> My Profile
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-xs hover:bg-rose-50 active:bg-rose-100 text-left text-rose-700 border-t border-slate-100"
                data-testid="m-menu-logout"
              >
                <LogOut size={14} /> Log Out
              </button>
            </div>
          )}
        </div>
      </header>
      {/* Outlet — bottom padding accounts for fixed bottom-nav height + Android
          gesture navigation bar so content never disappears behind the nav. */}
      <div
        className="flex-1"
        style={{ paddingBottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
      >
        <Outlet />
      </div>
      {/* Bottom tab-bar — also safe-area aware so it sits above the Android
          gesture-bar / iOS home-indicator instead of overlapping it. */}
      <nav
        className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-40 grid shadow-[0_-2px_8px_rgba(59,72,114,0.08)]"
        style={{
          gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        data-testid="mobile-bottom-nav"
      >
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={`m-tab-${t.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2.5 px-1 text-[10px] font-display font-bold uppercase tracking-wider gap-1 whitespace-nowrap min-w-0 ${
                isActive ? 'text-[#1F7A6E]' : 'text-slate-500'
              }`
            }
          >
            <t.icon size={20} />
            <span className="truncate w-full text-center leading-tight">{t.label}</span>
          </NavLink>
        ))}
      </nav>
      {showOnboarding && (
        <OnboardingWizard user={user} onClose={() => setShowOnboarding(false)} />
      )}
    </div>
  );
}
